import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../supabaseClient'; // Assure-toi que le chemin est correct
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FeaturedUniversities = () => {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- 1. CHARGEMENT DES DONNÉES (Top 4 par seuil) ---
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // On récupère les 4 écoles avec les seuils les plus hauts (les plus prestigieuses)
        // et on filtre celles qui ont un seuil défini (not null)
        const { data, error } = await supabase
          .from('schools')
          .select('*')
          .not('admission_threshold', 'is', null)
          .order('admission_threshold', { ascending: false })
          .limit(4);

        if (error) throw error;
        setInstitutions(data);
      } catch (error) {
        console.error("Erreur chargement vedettes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeatured();
  }, []);

  // --- 2. LOGIQUE D'IMAGE (Même que l'annuaire) ---
  const getSchoolImage = (school) => {
    if (school.logo_url) return school.logo_url;
    const categoryImages = {
      'Commerce & Gestion': 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80',
      'Ingénierie': 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80',
      'Santé': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80',
      'Classes Prépa (CPGE)': 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80',
      'Classes Prépa (Excellence)': 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=800&q=80',
      'Sciences & Techniques': 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=800&q=80',
      'Technologie (DUT)': 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800&q=80',
    };
    return categoryImages[school.category] || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=800&q=80';
  };

  // --- 3. HELPER POUR LES LOGOS (Remplacés par icônes si vide) ---
  // Comme on n'a pas encore de vrais logos, on affiche une icône stylisée
  const CategoryIcon = ({ category }) => {
    if (category?.includes('Santé')) return <Icon name="Activity" className="text-primary" size={24} />;
    if (category?.includes('Ingénierie')) return <Icon name="Cpu" className="text-primary" size={24} />;
    if (category?.includes('Commerce')) return <Icon name="TrendingUp" className="text-primary" size={24} />;
    return <Icon name="BookOpen" className="text-primary" size={24} />;
  };

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Établissements d'Excellence
            </h2>
            <p className="text-lg text-muted-foreground">
              Les écoles les plus sélectives du Maroc basées sur les seuils d'accès
            </p>
          </div>
          <Link
            to="/post-bac-database"
            className="hidden lg:flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
          >
            <span className="font-medium">Voir l'annuaire complet</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>

        {/* Content Grid */}
        {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-80 bg-muted/20 animate-pulse rounded-2xl"></div>
                ))}
            </div>
        ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {institutions.map((institution) => (
                <div
                key={institution.id}
                className="group block h-full"
                >
                <div className="bg-card rounded-2xl overflow-hidden shadow-soft border border-border hover:shadow-interactive transition-all duration-300 hover:scale-105 h-full flex flex-col">
                    {/* Image Header */}
                    <div className="relative h-48 overflow-hidden">
                    <Image
                        src={getSchoolImage(institution)}
                        alt={institution.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    
                    {/* Badge Catégorie au lieu du logo s'il n'existe pas */}
                    <div className="absolute top-4 left-4">
                        <div className="w-12 h-12 bg-white rounded-lg p-2 shadow-soft flex items-center justify-center">
                            {institution.logo_url ? (
                                <img src={institution.logo_url} alt="Logo" className="object-contain" />
                            ) : (
                                <CategoryIcon category={institution.category} />
                            )}
                        </div>
                    </div>

                    {/* Badge Type (Public/Privé) */}
                    <div className="absolute top-4 right-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            institution.type === 'Public' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                        {institution.type}
                        </span>
                    </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4 flex flex-col flex-grow">
                    <div>
                        <h3 className="font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 h-12">
                        {institution.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{institution.category}</p>
                    </div>

                    {/* Location */}
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Icon name="MapPin" size={14} />
                        <span>{institution.city}</span>
                    </div>

                    {/* Features Tags (Générés dynamiquement) */}
                    <div className="flex flex-wrap gap-1 mt-auto">
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                            Sélectif
                        </span>
                        <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                            Prestigieux
                        </span>
                    </div>

                    {/* Seuil d'admission (La vraie stat importante) */}
                    <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                        <span className="text-xs text-muted-foreground">Seuil moyen</span>
                        <span className="text-sm font-bold text-primary">
                            {institution.admission_threshold ? institution.admission_threshold.toFixed(2) : 'Dossier'} / 20
                        </span>
                    </div>
                    </div>
                </div>
                </div>
            ))}
            </div>
        )}

        {/* Mobile CTA */}
        <div className="text-center mt-8 lg:hidden">
          <Link
            to="/post-bac-database"
            className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
          >
            <span className="font-medium">Voir l'annuaire complet</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedUniversities;